"use client";

import {
   useState,
   useEffect,
   useCallback,
   useRef,
   useMemo,
   forwardRef,
   useImperativeHandle
} from "react";
import { useTranslations } from "next-intl";
import { Loader2, Check, X } from "lucide-react";
import {
   Select,
   SelectContent,
   SelectTrigger,
   SelectValue,
   Command,
   CommandEmpty,
   CommandGroup,
   CommandInput,
   CommandItem,
   CommandList,
   Button
} from "@/app/components/ui";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { api_url } from "@/app/api";
import { METHOD_TYPE } from "@/app/lib/enums";
import { PaginationInfo, UserProfile } from "@/types";

interface AutocompleteUserProps {
   value?: string;
   onValueChange?: (value: UserProfile | null) => void;
   placeholder?: string;
   disabled?: boolean;
   className?: string;
   clearable?: boolean;
}

export interface AutocompleteUserRef {
   clear: () => void;
}

const LIMIT = "1000";
const FOCUS_DELAY = 300;

export const AutocompleteUser = forwardRef<AutocompleteUserRef, AutocompleteUserProps>(
   (
      {
         value,
         onValueChange,
         placeholder,
         disabled = false,
         className,
         clearable = true
      },
      ref
   ) => {
      const translate = useTranslations("Auth.admin.FeedbackAnalysis");

      const [users, setUsers] = useState<UserProfile[]>([]);
      const [pagination, setPagination] = useState<PaginationInfo | null>(null);
      const [loading, setLoading] = useState(false);
      const [searchText, setSearchText] = useState("");
      const [open, setOpen] = useState(false);
      const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
      const searchInputRef = useRef<HTMLInputElement>(null);
      const isInitializedRef = useRef(false);
      const observerRef = useRef<IntersectionObserver | null>(null);

      const lastUserRef = useCallback(
         (node: HTMLDivElement | null) => {
            if (observerRef.current) observerRef.current.disconnect();
            if (loading) return;

            observerRef.current = new IntersectionObserver((entries) => {
               const isLoadMore = entries[0].isIntersecting && pagination?.hasNextPage;
               if (isLoadMore) {
                  const nextPage = pagination.currentPage + 1;
                  fetchUsers(nextPage, searchText);
               }
            });

            if (node) observerRef.current.observe(node);
         },
         [pagination?.hasNextPage, loading, searchText]
      );

      const fetchUsers = useCallback(async (page: number, search?: string) => {
         if (loading) return;
         setLoading(true);

         try {
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", LIMIT);
            if (search) {
               params.append("search", search);
            }

            const response = await fetchWithAuth(
               `${api_url.user_list_url}?${params.toString()}`,
               {
                  method: METHOD_TYPE.GET
               }
            );

            if (!response.ok) {
               throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const { users, pagination } = result;
            if (page === 1) {
               setUsers(users);
            } else {
               setUsers((prevUsers) => [...prevUsers, ...users]);
            }
            setPagination(pagination);
            if (value) {
               setSelectedUser(
                  users.find((user: UserProfile) => user.id === value) || null
               );
            }
         } catch (error) {
            console.error("Error fetching users:", error);
         } finally {
            setLoading(false);
         }
      }, []);

      useEffect(() => {
         if (!isInitializedRef.current) {
            setUsers([]);
            fetchUsers(1);
            isInitializedRef.current = true;
         }
      }, [fetchUsers]);

      useEffect(() => {
         if (open && searchInputRef.current) {
            const timer = setTimeout(() => {
               if (searchInputRef.current) {
                  searchInputRef.current.focus();
               }
            }, FOCUS_DELAY);
            return () => clearTimeout(timer);
         }
      }, [open]);

      const handleClear = useCallback(
         (e?: React.MouseEvent) => {
            e?.stopPropagation();
            setSelectedUser(null);
            onValueChange?.(null);
         },
         [onValueChange]
      );

      useImperativeHandle(
         ref,
         () => ({
            clear: handleClear
         }),
         [handleClear]
      );

      const handleUserSelect = useCallback(
         (user: UserProfile) => {
            setSelectedUser(user);
            onValueChange?.(user);
            setOpen(false);
            setSearchText("");
         },
         [onValueChange]
      );
      return (
         <div className={className}>
            <div className="relative">
               <Select
                  value={selectedUser?.id}
                  open={open}
                  onOpenChange={setOpen}
                  disabled={disabled}
               >
                  <SelectTrigger className="pr-8">
                     <SelectValue placeholder={placeholder || translate("searchUser")}>
                        {selectedUser && (
                           <div className="flex items-center gap-2 pr-2">
                              <span>{getUserDisplayEmail(selectedUser)}</span>
                           </div>
                        )}
                     </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="p-0">
                     <Command>
                        <CommandInput
                           ref={searchInputRef}
                           placeholder={placeholder || translate("searchUser")}
                           disabled={loading}
                        />
                        <CommandList>
                           <CommandEmpty>
                              {loading ? (
                                 <div className="flex items-center justify-center p-6">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                    <span className="ml-2">
                                       {translate("loadingUsers")}
                                    </span>
                                 </div>
                              ) : searchText ? (
                                 <div className="p-6 text-center text-muted-foreground">
                                    {translate("noUsersFoundMatching", {
                                       searchText
                                    })}
                                 </div>
                              ) : (
                                 <div className="p-6 text-center text-muted-foreground">
                                    {translate("noUsersAvailable")}
                                 </div>
                              )}
                           </CommandEmpty>
                           <CommandGroup>
                              {users.map((user, index) => (
                                 <UserItem
                                    key={user.id}
                                    user={user}
                                    selectedUser={selectedUser}
                                    onUserSelect={handleUserSelect}
                                    lastUserRef={
                                       index === users.length - 1 ? lastUserRef : null
                                    }
                                 />
                              ))}
                           </CommandGroup>
                        </CommandList>
                     </Command>
                  </SelectContent>
               </Select>
               {clearable && selectedUser && (
                  <Button
                     variant="ghost"
                     size="icon"
                     onClick={handleClear}
                     className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center hover:bg-destructive/10 hover:text-destructive rounded-full transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-destructive/20 focus:ring-offset-1 z-10 group bg-background border border-input"
                     aria-label="Clear selection"
                  >
                     <X className="h-4 w-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                  </Button>
               )}
            </div>
         </div>
      );
   }
);

AutocompleteUser.displayName = "AutocompleteUser";

type UserItemProps = {
   user: UserProfile;
   selectedUser: UserProfile | null;
   onUserSelect: (user: UserProfile) => void;
   lastUserRef: ((node: HTMLDivElement | null) => void) | null;
};
const UserItem = ({ user, selectedUser, onUserSelect, lastUserRef }: UserItemProps) => {
   const value = useMemo(() => {
      return (
         (user.metaData?.name ?? "") +
         " " +
         (user.email ?? "") +
         " " +
         (user.metaData?.role ?? "")
      );
   }, [user]);
   const isStudent = user.metaData?.isStudent;
   const isAdmin = user.metaData?.role?.includes("admin");
   return (
      <CommandItem
         ref={lastUserRef}
         key={user.id}
         value={value}
         onSelect={() => {
            onUserSelect(user);
         }}
         className="cursor-pointer"
      >
         <Check
            className={`mr-2 h-4 w-4 ${
               user.id === selectedUser?.id ? "opacity-100" : "opacity-0"
            }`}
         />
         <div className="flex flex-col">
            <span className="font-medium">{getUserDisplayEmail(user)}</span>
            <span className="text-sm text-muted-foreground">
               {user?.metaData?.display_name || user?.username}
               {isStudent ? " - student" : isAdmin ? " - admin" : ""}
            </span>
         </div>
      </CommandItem>
   );
};
const getUserDisplayEmail = (user: UserProfile) => {
   if (user.email) return user.email;
   if (user.metaData?.display_name) return user.metaData.display_name;
   if (user.metaData?.name) return user.metaData.name;
   if (user.metaData?.given_name && user.metaData?.family_name) {
      return `${user.metaData.given_name} ${user.metaData.family_name}`;
   }
   return user.username;
};
